import React from 'react';

import { Box, Button, Checkbox, Group, Paper, Title, clsx, Text } from '@mantine/core';
import GoBackButton from 'components/BackButton/GoBackButton';
import AthleteMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/AthleteMultiSelectAutoComplete';
import EventTypeMultiSelectAutoComplete from 'components/form-fields/auto-complete-fields/EventTypeMultiSelectAutoComplete';
import PackageMultiSelect from 'components/form-fields/auto-complete-fields/PackageMultiSelect';
import DateRangePicker, { Period } from 'components/form-fields/range-pickers/DateRangePicker';
import PercentageRangePicker from 'components/form-fields/range-pickers/PercentageRangePicker';
import { useSearchFilters } from 'pages/private/owner/reports/PackageUtilization/SearchFiltersContext';
import { EventType } from 'types';

import ClearFilters from './Options/ClearFilters';
import DownloadCSV from './Options/DownloadCSV';
import useStyles from './useStyles';
import { PackageUtilizationSearchFilters } from '../../SearchFilters';
import { useModal } from '../ManagePackagesModal/Context/ModalContext';

type GroupByFilters = Pick<PackageUtilizationSearchFilters, 'groupByAthlete' | 'groupByPackage'>;

const PackageUtilizationPageHeader = (): JSX.Element => {
  const { searchFilters, setSearchFilters, resetFilters } = useSearchFilters();

  const { classes } = useStyles();
  const { openModal } = useModal();

  const handleGroupBy = (key: keyof GroupByFilters) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = !!e.currentTarget?.checked;
    setSearchFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleEventTypesChange = (values: EventType[]): void => {
    setSearchFilters(prev => ({ ...prev, eventTypes: values }));
  };

  const handlePackagesChange = (values: string[]): void => {
    setSearchFilters(prev => ({ ...prev, packageIds: values }));
  };

  const handleAthletesChange = (values: string[]): void => {
    setSearchFilters(prev => ({ ...prev, athleteIds: values }));
  };

  const handleUtilizationRateChange = (value: [number, number] | null): void => {
    setSearchFilters(prev => ({ ...prev, utilizationRateRange: value }));
  };

  const handleDateRangeChange = (value: Period): void => {
    setSearchFilters(prev => ({
      ...prev,
      period: value.period,
      startDate: value.startDate,
      endDate: value.endDate,
    }));
  };

  return (
    <Paper className={clsx(classes.baseHeader, classes.headerWithExpectations)}>
      <Group noWrap spacing='sm' className={classes.titleSection}>
        <GoBackButton />
        <Title className={classes.title}>Package Utilization</Title>
      </Group>
      <Button className={classes.manageTracking} variant='outline' onClick={() => openModal('list')}>
        <Text className={classes.manageTrackingText}>Manage Packages to Track</Text>
      </Button>
      <Box className={classes.filters}>
        <DateRangePicker
          className={classes.dateRangeFilter}
          presets={['last_week', 'last_7_days', 'this_month', 'last_month', 'past_12_months']}
          placeholder='Date Range'
          value={{
            period: searchFilters.period,
            startDate: searchFilters.startDate,
            endDate: searchFilters.endDate,
          }}
          onChange={handleDateRangeChange}
        />
        <PercentageRangePicker
          className={classes.utilizationFilter}
          value={searchFilters.utilizationRateRange}
          onChange={handleUtilizationRateChange}
          placeholder='Utilization Rate'
        />
        <AthleteMultiSelectAutoComplete
          className={classes.athletesFilter}
          value={searchFilters.athleteIds ?? []}
          onChange={handleAthletesChange}
          placeholder='Athlete Name'
          label=''
        />
        <PackageMultiSelect
          className={classes.packagesFilter}
          value={searchFilters.packageIds ?? []}
          onChange={handlePackagesChange}
          placeholder='Package Name'
          includeArchived={true}
        />
        <EventTypeMultiSelectAutoComplete
          placeholder='Event Type'
          handleChange={handleEventTypesChange}
          values={searchFilters.eventTypes}
          className={classes.eventTypesFilter}
        />
        <Checkbox
          label='Group by athlete'
          className={classes.groupByAthlete}
          checked={searchFilters.groupByAthlete}
          onChange={handleGroupBy('groupByAthlete')}
        />
        <Checkbox
          label='Group by package'
          className={classes.groupByPackage}
          checked={searchFilters.groupByPackage}
          onChange={handleGroupBy('groupByPackage')}
        />
      </Box>
      <ClearFilters className={classes.clear} onClick={resetFilters} />
      <DownloadCSV className={classes.download} searchFilters={searchFilters} />
    </Paper>
  );
};

export default PackageUtilizationPageHeader;
